// Inside AttendanceLogList.jsx

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Chip, Button, Divider, LinearProgress
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { useEmployeeService } from "../../services/EmployeeService";
import FilterHeader from "../../components/FilterHeader";



const AttendanceLogList = ({ empID = "All" }) => {
  const { fetchAttendanceLogs } = useEmployeeService();

  const [filters, setFilters] = useState({
    VerticalDescriptions: "All",
    ProjectDescriptions: "All",
    Locations: "All",
  });

  const ROW_HEIGHT = empID === "All" ? 32 : 28;
  const [search, setSearch] = useState({});
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const listRef = useRef();

  const columnDefs = useMemo(() => {
    const base = [
      { key: "SessionStartFormatted", label: "Check In", flex: 2 },
      { key: "SessionEndFormatted", label: "Check Out", flex: 2 },
      { key: "LocationName", label: "Location", flex: 2.5 },
    //   { key: "Address", label: "Address", flex: 2 },
    //   { key: "Business_Vertical_Desc", label: "Vertical", flex: 1 },
    //   { key: "Business_Sub_Vertical_Description", label: "Sub Vertical", flex: 2 },
    //   { key: "Project_Description", label: "Project", flex: 1 },
      { key: "InLatLong", label: "Lat, Long (In)", flex: 1.5 },
      { key: "OutLatLong", label: "Lat, Long (Out)", flex: 1.5 },
    ];
    if (empID === "All") {
      base.unshift(
        { key: "UserGuid", label: "User ID", flex: 1 },
        { key: "UserName", label: "Name", flex: 2 }
      );
    }
    return base;
  }, [empID]);

  const fetchData = async () => {
    if (startDate.isAfter(endDate)) {
      alert("Start Date must be before or equal to End Date");
      return;
    }

    setLoading(true);
    try {
      const res = await fetchAttendanceLogs({
        employeeId: empID,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      });

      const filtered = res.map(r => ({
        ...r,
        LocationName: r.LocationName && r.Address ? `${r.LocationName}, ${r.Address}` : "",
        InLatLong: r.Latitude && r.Longitude
            ? `${Number(r.Latitude).toFixed(5)}, ${Number(r.Longitude).toFixed(5)}`
            : "",
            OutLatLong: r.CheckOutLatitude && r.CheckOutLongitude
            ? `${Number(r.CheckOutLatitude).toFixed(5)}, ${Number(r.CheckOutLongitude).toFixed(5)}`
            : "",
      }));

      setData(empID === "All" ? filtered : filtered.filter(d => d.UserGuid === empID));
    } catch (err) {
      setError(err.message || "Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchVertical = filters.VerticalDescriptions === "All" || item.Business_Vertical_Desc === filters.VerticalDescriptions;
      const matchProject = filters.ProjectDescriptions === "All" || item.Project_Description === filters.ProjectDescriptions;
      const matchLocation = filters.Locations === "All" || item.Business_Sub_Vertical_Description === filters.Locations;

      const matchesSearch = columnDefs.every(({ key }) => {
        const term = search[key]?.toLowerCase() || "";
        if (!term) return true;
        const value = item[key];
        return value?.toString().toLowerCase().includes(term);
      });

      return matchVertical && matchProject && matchLocation && matchesSearch;
    });
  }, [data, filters, search, columnDefs]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];
    return (
      <Box key={index} style={style} sx={{ display: "flex", alignItems: "center", borderBottom: "1px solid #eee", px: 0.8 }}>
        {columnDefs.map(({ key, flex }) => (
          <Box
            key={key}
            sx={{
              flex,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              pr: 1,
              fontSize: "0.875rem",
            }}
          >
            {row[key]}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant={empID === "All" ? "h2" : "h6" } sx={{ minWidth: 180 }}>
          Attendance Logs
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              {empID === "All" && ( 
                     <FilterHeader filters={filters} setFilters={setFilters} /> 
            )}
          <DatePicker
            label="Start Date"
            value={startDate}
            format="DD-MMM-YYYY"
            onChange={(date) => setStartDate(date)}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            format="DD-MMM-YYYY"
            onChange={(date) => setEndDate(date)}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button variant="contained" onClick={fetchData}>Fetch Logs</Button>
        </Box>
      </Box>

    

      <Divider sx={{ my: 0.5 }} />

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Typography color="error">Error: {error}</Typography>
      ) : (
        <Box component={Paper} sx={{ overflow: "auto" }}>
          <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) => setSearch((prev) => ({ ...prev, [key]: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
          </Box>

          {filteredData.length > 0 ? (
            <Box sx={{ height: empID === "All" ? "70vh" : "30vh"}}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    width={width}
                    itemSize={() => ROW_HEIGHT}
                    itemCount={filteredData.length}
                    itemKey={(index) => filteredData[index].UserGuid + index}
                    ref={listRef}
                  >
                    {Row}
                  </List>
                )}
              </AutoSizer>
            </Box>
          ) : (
            <Box p={2} textAlign="center" color="text.secondary">
              No matching records found.
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AttendanceLogList;
